import { useState, useRef, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import NavBar from '../../../../component/b2c/navBar/navBar';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import { useForm } from 'react-hook-form';
import { IoMdArrowRoundBack } from "react-icons/io";
import {
    FaCalculator, FaUser, FaShoppingCart, FaBox, FaWarehouse, FaEnvelope,
    FaPhoneAlt, FaMapMarkerAlt, FaHashtag, FaCity, FaFlag, FaRupeeSign,
    FaWallet, FaClipboardList, FaWeightHanging, FaBoxOpen, FaRulerCombined
} from 'react-icons/fa';
import { useMyContext } from '../../../../context/MyContext';
import GoogleAddressInput from "../../../../component/autoFillAddress/addressAutoFill";
import { Controller } from "react-hook-form";
import '../../../../css/custom-scrollbar.css';
import Select from "react-dropdown-select";
import ViewRateForword from '../../../../component/b2c/models/ViewRateForwordShipmet';
import { Base_Url, getpincode, addShipment, allwarehouseslist } from '../../../../config/config';
import { toast } from 'react-toastify';
import Spinner from '../../../../component/spinner/spinner';
import axios from 'axios';
import UploadBulk from '../../../../component/b2c/models/UploadBulk';
import FullPageLoader from '../../../../component/fullPageLoader/fullPageLoader';

const schema = z.object({
    warehouse: z.string().nonempty("Warehouse is required"),
    firstName: z
        .string()
        .trim()
        .min(1, "First Name is required")
        .regex(/^[a-zA-Z\s\-'\.]+$/, "First Name must only contain alphabetic characters")
        .refine((val) => val.trim().length > 0, {
            message: "First Name cannot be only spaces",
        }),
    lastName: z
        .string()
        .trim()
        .min(1, "Last Name is required")
        .regex(/^[a-zA-Z\s\-'\.]+$/, "Last Name must only contain alphabetic characters")
        .refine((val) => val.trim().length > 0, {
            message: "Last Name cannot be only spaces",
        }),
    email: z
        .string()
        .optional()
        .refine((val) => !val || z.string().email().safeParse(val).success, {
            message: "Please enter a valid email address",
        }),
    mobile: z
        .string()
        .trim()
        .refine((val) => /^\d{10}$/.test(val), {
            message: "Mobile number must be exactly 10 digits",
        }),
    address: z
        .string()
        .min(1, "Address is required")
        .regex(/^[^#\\%&;@]*$/, "Address contains invalid characters (# \\ % & @ ; not allowed)"),
    pincode: z
        .string()
        .trim()
        .refine((val) => /^\d{6}$/.test(val), {
            message: "Pincode must be exactly 6 digits",
        }),
    city: z
        .string()
        .trim()
        .min(1, "City is required")
        .regex(/^[a-zA-Z][a-zA-Z\s().,'-]*$/, "City contains invalid characters"),
    state: z
        .string()
        .trim()
        .min(1, "State is required")
        .regex(/^[a-zA-Z\s\-]+$/, "State must only contain alphabetic characters"),
    orderId: z
        .string()
        .trim()
        .min(1, "Order ID is required")
        .refine((val) => val.trim().length > 0, {
            message: "Order ID cannot be only spaces",
        }),
    invoiceAmount: z
        .string()
        .trim()
        .min(1, "Invoice Amount is required")
        .refine((val) => val.trim().length > 0 && /^\d*\.?\d+$/.test(val), {
            message: "Invoice Amount must be a valid number",
        }),
    paymentMode: z
        .string()
        .trim()
        .min(1, "Payment Mode is required")
        .refine((val) => val.trim().length > 0, {
            message: "Payment Mode cannot be only spaces",
        }),
    productDes: z
        .string()
        .trim()
        .min(1, "Product Description is required")
        .refine((val) => val.trim().length > 0, {
            message: "Product Description cannot be only spaces",
        }),
    codAmount: z
        .string()
        .optional()
        .refine((val) => !val || (val.trim().length > 0 && !/^\s+$/.test(val)), {
            message: "COD Amount cannot be only spaces",
        }),
    weight: z.preprocess(
        (val) => (val === '' || val === undefined || val === null) ? undefined : Number(val),
        z.number()
            .int("Weight must be a whole number")
            .min(50, "Weight must be at least 50 gm")
    ),
    length: z
        .string()
        .min(1, "Length is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, {
            message: "Length must be a positive number"
        }),
    width: z
        .string()
        .min(1, "Width is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, {
            message: "Width must be a positive number"
        }),
    height: z
        .string()
        .min(1, "Height is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, {
            message: "Height must be a positive number"
        }),
});

const errorText = (msg) => (
    <p className="text-red-500 text-[8px] mt-0.5 font-medium flex items-center gap-1">
        <span className="w-0.5 h-0.5 rounded-full bg-red-500 inline-block" />
        {msg}
    </p>
);

const Field = ({ icon: Icon, label, error, children, isDarkMode }) => (
    <div>
        <label className={`block text-[10px] font-medium  tracking-wider mb-0.5 ${isDarkMode ? "text-gray-600" : "text-gray-700"}`}>
            {label}
        </label>
        <div className="relative group">
            <Icon
                size={10}
                className={`absolute left-2.5 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                    ${isDarkMode ? "text-gray-500 group-focus-within:text-white" : "text-gray-400 group-focus-within:text-black"}`}
            />
            {children}
        </div>
        {error && errorText(error)}
    </div>
);

const SectionCard = ({ number, icon: Icon, title, subtitle, children, className = "", isDarkMode }) => (
    <div
        className={`relative overflow-hidden rounded-lg border p-2.5 transition-colors
            ${isDarkMode
                ? "bg-gray-300/50 border-gray-700"
                : "bg-gray-300/10 border-gray-300"
            } ${className}`}
    >
        <div className="relative flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
                <span className={`flex items-center justify-center w-6 h-6 rounded-lg shrink-0 border
                    ${isDarkMode
                        ? "bg-blue-600 text-white border-gray-600"
                        : "bg-blue-600 text-white border-gray-300"
                    }`}>
                    <Icon size={11} />
                </span>
                <div>
                    <h2 className={`text-[12px] font-bold tracking-wide ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                        {title}
                    </h2>
                    {subtitle && (
                        <p className={`text-[10px] mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}>
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        </div>
        <div className="relative">{children}</div>
    </div>
);

export default function ForwordShipment() {
    const [pageLoading, setPageLoading] = useState(false);
    const { isDarkMode, isToggleSidebar } = useMyContext()
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [uploadBulkPopupOpen, setUploadBulkPopupOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tableData, setTableData] = useState(null);
    const Navigation = useNavigate();
    const [wareHouseList, setwareHouseList] = useState([])
    const [wareHouseId, setwareHouseId] = useState('')
    const [wareHousePincode, setwareHousePincode] = useState('');

    const [reqBody, setReqBody] = useState([]);
    const fileInputRef = useRef(null);

    const { register, handleSubmit, formState: { errors }, reset, watch, trigger, setValue, control } = useForm({
        resolver: zodResolver(schema),
    });


    const handleViewClick = () => {
        setIsPopupOpen(true);
    };
    const handleClose = () => {
        setIsPopupOpen(false);
    };

    const UploadBulkViewClick = () => {
        setUploadBulkPopupOpen(true);

    }

    const handleCloseUploadBulk = () => {
        setUploadBulkPopupOpen(false);

    };

    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');


    const pincode = watch("pincode");

    useEffect(() => {
        if (pincode && pincode.length === 6) {
            fetchPincodeData(pincode);
        }
    }, [pincode]);


    const fetchPincodeData = async (pincode) => {
        setPageLoading(true)
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pincode}`;
        try {
            const response = await fetch(urlgetPincode, {
                method: 'GET',
                headers: {
                    'token': token,
                    'cus_id': cus_id,
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();
            if (data && data.status === "SUCCESS") {
                let { city, state } = data.data[0];
                city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
                state = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
                setValue("city", city);
                setValue("state", state);
                setPageLoading(false)
            } else if (data && data.status === "FAIL") {
                setValue("city", "");
                setValue("state", "");
                toast.error(data.message);
                setPageLoading(false)
            } else {
                toast.error("Failed to fetch city and state data");
                setPageLoading(false)
            }

        } catch (error) {
            console.error('Error fetching pincode data:', error);
            toast.error("Error fetching pincode data");
            setPageLoading(false)
        }
    };

    const paymentMode = watch("paymentMode");

    useEffect(() => {
        if (paymentMode === "prepaid") {
            setValue("codAmount", "0");
        } else if (paymentMode === "cod") {
            setValue("codAmount", "");
        }
    }, [paymentMode, setValue]);

    const handleInputChange = (event) => {
        const { value, id } = event.target;

        if (['pincode', 'mobile', 'boxQty', 'orderId'].includes(id)) {
            const filteredValue = value.replace(/[^0-9]/g, '');
            event.target.value = filteredValue;

            if (id === "pincode") {
                if (filteredValue.length < 6) {
                    setValue("city", "");
                    setValue("state", "");
                } else if (filteredValue.length === 6) {
                    fetchPincodeData(filteredValue);
                }
            }
        }

        if (['firstName', 'lastName', 'city', 'state'].includes(id)) {
            const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
            event.target.value = filteredValue;
        }
    };


    useEffect(() => {
        fetchWareHouseList()
    }, [])

    const fetchWareHouseList = async () => {
        const token = localStorage.getItem('token');
        const cusid = localStorage.getItem('cus_id');
        const headers = new Headers({
            'token': token,
            'cus_id': cusid,
            'Content-Type': 'application/json'
        });

        try {
            const response = await fetch(Base_Url + allwarehouseslist, {

                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const transformedData = data.data.map(warehouse => ({
                value: warehouse.id,
                label: warehouse.name,
                pincode: warehouse.pincode
            }));
            setwareHouseList(transformedData)
        } catch (error) {
            console.error('Error:', error);
        }
    }

    const handleNaviagtion = () => {
        Navigation("/dashboard");

    }

    const onSubmit = async (data) => {
        const { weight } = data;

        if (weight > 20000) {
            Swal.fire({
                title: "Weight Limit Exceeded!",
                text: "The weight cannot exceed 20kg. Please adjust the value.",
                icon: "error",
                confirmButtonText: "Okay",
            });
            return;
        }
        setPageLoading(true);
        setIsSubmitting(true);
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const requestBody = {
            customer: {
                first_name: data.firstName,
                last_name: data.lastName,
                email: data.email,
                phone: data.mobile,
                address_type: "home",
            },
            droplocation: {
                address: data.address,
                city: data.city,
                state: data.state,
                country: "India",
                pin: data.pincode,
            },
            pickuplocation: {
                warehouse_id: wareHouseId,
            },
            product: {
                product_desc: data.productDes,
                quantity: "1",
                total_amount: data.invoiceAmount,
                cod_amount: data.codAmount,
                payment_mode: data.paymentMode,
                shipment_type: "Forward"
            },
            dimension: {
                length: data.length,
                width: data.width,
                height: data.height,
                weight: data.weight,
            },
        };
        setReqBody(requestBody);
        try {
            const response = await axios.post(Base_Url + addShipment, requestBody, {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                toast.success(response.data.message);
                setTableData(response.data);
                handleViewClick();
                Navigation("/shipments/all");
                setIsSubmitting(false);
                setPageLoading(false);
            } else {
                toast.error(response.data.message || "Something went wrong. Please check the data.");
                setIsSubmitting(false);
                setPageLoading(false);
            }

        } catch (error) {
            const messages = error?.response?.data?.messages;

            let apiError = "Something went wrong!";

            if (typeof messages === "string") {
                apiError = messages;
            } else if (typeof messages === "object" && messages !== null) {
                const firstKey = Object.keys(messages)[0];
                apiError = messages[firstKey];
                setIsSubmitting(false);
                setPageLoading(false);
            } else {
                apiError = error?.message || "Something went wrong!";
                setIsSubmitting(false);
                setPageLoading(false);
            }
            toast.error(apiError);
            setIsSubmitting(false);
            setPageLoading(false);
        }
    };

    const inputBase = `w-full pl-7 pr-2 py-1.5 text-[10px] rounded-md border transition-all duration-200 outline-none
        placeholder:text-gray-400 
        ${isDarkMode
            ? "bg-gray-900/50 border-gray-700 text-white placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
            : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
        }`;
    const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";


    return (
        <>
            <div
                className={`h-screen flex flex-col overflow-hidden relative ${isDarkMode ? "bg-black" : "bg-gray-50"}`}
            >
                <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />
                {/* <div className="pointer-events-none absolute top-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-sky-300/30 dark:bg-sky-500/10 blur-3xl" /> */}
                {/* <div className="pointer-events-none absolute bottom-0 left-1/3 h-96 w-96 rounded-full bg-pink-300/20 dark:bg-fuchsia-500/10 blur-3xl" /> */}



                <NavBar />
                <div className="flex flex-1 overflow-hidden">
                    {isToggleSidebar && (
                        <div className={`w-64 m-3 rounded-3xl ${glass} overflow-hidden`}>
                            <Sidebar />
                        </div>
                    )}

                    <div
                        className={`flex-1 overflow-auto relative bg-white  dark:bg-black transition-all duration-300 custom-scrollbar ${isDarkMode ? "text-white" : "text-black"}`}
                    >
                        <div className="container mx-auto px-3 pt-3 pb-4 overflow-x-hidden relative">

                            {/* PAGE HEADER */}
                            <div className="mb-3 flex flex-col md:flex-row md:items-end md:justify-between gap-2">
                                <div>
                                    <p className={`text-[7px] font-bold uppercase tracking-[0.2em] mb-0.5 inline-block px-2 py-1.5 rounded-lg
                                        ${isDarkMode ? "text-black bg-white" : "text-white bg-blue-600"}`}>
                                        B2C · New Booking
                                    </p>
                                    <h1 className={`text-sm md:text-base font-bold tracking-tight ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                        Create Forward Shipment
                                    </h1>
                                    {/* <p className={`text-[8px]  ${isDarkMode ? "text-gray-600" : "text-gray-600"}`}>
                                        Fill in customer, order and package details to generate a new shipment.
                                    </p> */}
                                </div>

                                <div className="flex flex-col md:flex-row gap-1.5 md:shrink-0">
                                    <button
                                        className={`w-full md:w-auto px-3 py-1.5 rounded-lg flex items-center justify-center font-semibold text-[9px] border transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 shadow-sm
                                            ${isDarkMode
                                                ? "bg-white text-black border-gray-600 hover:bg-gray-200"
                                                : "bg-blue-600 text-white border-gray-300 hover:bg-gray-700"
                                            }`}
                                    >
                                        <IoMdArrowRoundBack className="mr-1.5" size={10} />
                                        Create Forward Order
                                    </button>

                                    <button
                                        onClick={UploadBulkViewClick}
                                        className={`w-full md:w-auto px-3 py-1.5 rounded-lg flex items-center justify-center font-semibold text-[9px] border transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0
                                            ${isDarkMode
                                                ? "bg-gray-800/50 border-gray-700 text-white hover:bg-gray-800"
                                                : "bg-gray-100 border-gray-600 text-gray-700 hover:bg-gray-200"
                                            }`}
                                    >
                                        <FaBox className="mr-1.5" size={10} />
                                        Upload Bulk
                                    </button>
                                </div>
                            </div>

                            <UploadBulk isopen={uploadBulkPopupOpen} onClose={handleCloseUploadBulk} />

                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* CUSTOMER DETAILS */}
                                <SectionCard number="01" icon={FaUser} title="Customer Details" subtitle="Who is this shipment going to" className="mb-2.5" isDarkMode={isDarkMode}>
                                    <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                                        <Field icon={FaWarehouse} label="Warehouse" error={errors.warehouse?.message} isDarkMode={isDarkMode}>
                                            <select
                                                id="warehouse"
                                                {...register("warehouse")}
                                                className={inputBase}
                                                onChange={(e) => {
                                                    const selectedValue = e.target.value;
                                                    const selectedItem = wareHouseList.find((item) => item.value === selectedValue);

                                                    if (selectedItem) {
                                                        setwareHouseId(selectedItem.value);
                                                        setwareHousePincode(selectedItem.pincode);
                                                    }

                                                    trigger("warehouse");
                                                    setValue("warehouse", selectedValue, { shouldValidate: true });
                                                }}
                                            >
                                                <option value="" className={isDarkMode ? "bg-black" : ""}>Select Warehouse</option>
                                                {wareHouseList.map((item) => (
                                                    <option key={item.value} value={item.value} className={isDarkMode ? "bg-black" : ""}>
                                                        {item.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>

                                        <Field icon={FaUser} label="First Name" error={errors.firstName?.message} isDarkMode={isDarkMode}>
                                            <input
                                                {...register("firstName")}
                                                placeholder='Enter First Name'
                                                className={`${inputBase} ${errors.firstName ? '!border-red-500/70' : ''}`}
                                            />
                                        </Field>

                                        <Field icon={FaUser} label="Last Name" error={errors.lastName?.message} isDarkMode={isDarkMode}>
                                            <input
                                                {...register("lastName")}
                                                placeholder='Enter Last Name'
                                                className={`${inputBase} ${errors.lastName ? '!border-red-500/70' : ''}`}
                                            />
                                        </Field>

                                        <Field icon={FaEnvelope} label="Email" error={errors.email?.message} isDarkMode={isDarkMode}>
                                            <input
                                                {...register("email")}
                                                placeholder='Enter Email'
                                                type="email"
                                                className={`${inputBase} ${errors.email ? '!border-red-500/70' : ''}`}
                                            />
                                        </Field>

                                        <Field icon={FaPhoneAlt} label="Mobile" error={errors.mobile?.message} isDarkMode={isDarkMode}>
                                            <input
                                                {...register("mobile")}
                                                placeholder='Enter Mobile No'
                                                maxLength={10}
                                                className={`${inputBase} ${errors.mobile ? '!border-red-500/70' : ''}`}
                                            />
                                        </Field>

                                        <Field icon={FaMapMarkerAlt} label="Address" error={errors.address?.message} isDarkMode={isDarkMode}>
                                            <input
                                                {...register("address")}
                                                placeholder='Enter Address'
                                                className={`${inputBase} ${errors.address ? '!border-red-500/70' : ''}`}
                                            />
                                        </Field>

                                        <Field icon={FaHashtag} label="Pincode" error={errors.pincode?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="pincode"
                                                {...register("pincode", {
                                                    onChange: handleInputChange,
                                                })}
                                                placeholder='Enter Pincode'
                                                maxLength={6}
                                                className={`${inputBase} ${errors.pincode ? '!border-red-500/70' : ''}`}
                                            />
                                        </Field>

                                        <Field icon={FaCity} label="City" error={errors.city?.message} isDarkMode={isDarkMode}>
                                            <input
                                                {...register("city")}
                                                placeholder='Enter City'
                                                className={`${inputBase} ${errors.city ? '!border-red-500/70' : ''}`}
                                            />
                                        </Field>

                                        <Field icon={FaFlag} label="State" error={errors.state?.message} isDarkMode={isDarkMode}>
                                            <input
                                                {...register("state")}
                                                placeholder='Enter State'
                                                className={`${inputBase} ${errors.state ? '!border-red-500/70' : ''}`}
                                            />
                                        </Field>
                                    </div>
                                </SectionCard>

                                {/* ORDER + BOX DETAILS */}
                                <div className="flex flex-col md:flex-row gap-2.5">
                                    {/* ORDER DETAILS */}
                                    <SectionCard number="02" icon={FaShoppingCart} title="Order Details" subtitle="Invoice & payment info" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <Field icon={FaHashtag} label="Order ID" error={errors.orderId?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    {...register("orderId")}
                                                    placeholder='Enter Order Id'
                                                    className={`${inputBase} ${errors.orderId ? '!border-red-500/70' : ''}`}
                                                />
                                            </Field>

                                            <Field icon={FaRupeeSign} label="Invoice Amount" error={errors.invoiceAmount?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    placeholder='Enter Invoice Amount'
                                                    {...register("invoiceAmount")}
                                                    className={`${inputBase} ${errors.invoiceAmount ? '!border-red-500/70' : ''}`}
                                                />
                                            </Field>

                                            <Field icon={FaWallet} label="Payment Mode" error={errors.paymentMode?.message} isDarkMode={isDarkMode}>
                                                <select
                                                    {...register("paymentMode")}
                                                    className={`${inputBase} ${errors.paymentMode ? '!border-red-500/70' : ''}`}
                                                >
                                                    <option value="" className={isDarkMode ? "bg-black" : ""}>Select Payment Mode</option>
                                                    <option value="prepaid" className={isDarkMode ? "bg-black" : ""}>Prepaid</option>
                                                    <option value="cod" className={isDarkMode ? "bg-black" : ""}>COD</option>
                                                </select>
                                            </Field>

                                            <Field icon={FaRupeeSign} label="COD Amount" error={errors.codAmount?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    {...register("codAmount")}
                                                    disabled={paymentMode !== "cod"}
                                                    placeholder='Enter Cod Amount'
                                                    className={`${inputBase} ${errors.codAmount ? '!border-red-500/70' : ''} disabled:opacity-40 disabled:cursor-not-allowed`}
                                                />
                                            </Field>

                                            <div className="md:col-span-2">
                                                <Field icon={FaClipboardList} label="Product Description" error={errors.productDes?.message} isDarkMode={isDarkMode}>
                                                    <input
                                                        {...register("productDes")}
                                                        placeholder='Enter Product Description'
                                                        className={`${inputBase} ${errors.productDes ? '!border-red-500/70' : ''}`}
                                                    />
                                                </Field>
                                            </div>
                                        </div>
                                    </SectionCard>

                                    {/* BOX DETAILS */}
                                    <SectionCard number="03" icon={FaBox} title="Package Details" subtitle="Weight & dimensions in grams / cm" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                            <Field icon={FaWeightHanging} label="Weight" error={errors.weight?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    {...register("weight")}
                                                    type="number"
                                                    placeholder='Enter Weight'
                                                    step="0.01"
                                                    className={`${inputBase} ${errors.weight ? '!border-red-500/70' : ''}`}
                                                />
                                            </Field>

                                            <Field icon={FaBoxOpen} label="Box Quantity" isDarkMode={isDarkMode}>
                                                <input
                                                    value={1}
                                                    disabled
                                                    className={`${inputBase} opacity-50 cursor-not-allowed`}
                                                />
                                            </Field>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                            <Field icon={FaRulerCombined} label="Length" error={errors.length?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    placeholder='Enter Length'
                                                    {...register("length")}
                                                    className={`${inputBase} ${errors.length ? '!border-red-500/70' : ''}`}
                                                />
                                            </Field>

                                            <Field icon={FaRulerCombined} label="Width" error={errors.width?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    placeholder='Enter Width'
                                                    {...register("width")}
                                                    className={`${inputBase} ${errors.width ? '!border-red-500/70' : ''}`}
                                                />
                                            </Field>

                                            <Field icon={FaRulerCombined} label="Height" error={errors.height?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    {...register("height")}
                                                    placeholder='Enter height'
                                                    className={`${inputBase} ${errors.height ? '!border-red-500/70' : ''}`}
                                                />
                                            </Field>
                                        </div>
                                    </SectionCard>
                                </div>

                                {/* SUBMIT */}
                                <div className="flex justify-center mt-3">
                                    <button
                                        type="submit"
                                        className={`group relative w-full md:w-[42%] overflow-hidden py-1.5 font-bold text-[10px] rounded-lg flex items-center justify-center gap-1.5 border hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 shadow-sm
                                            ${isDarkMode
                                                ? "bg-white text-black border-gray-600 hover:bg-gray-200"
                                                : "bg-blue-600 text-white border-gray-300 hover:bg-gray-700"
                                            }`}
                                    >
                                        <span className={`absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent ${isDarkMode ? "via-black/10" : "via-white/20"} to-transparent`} />
                                        <span className="relative flex items-center gap-1.5">
                                            {isSubmitting ? <Spinner /> : "Add Shipment"}
                                            <FaCalculator size={10} />
                                        </span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <ViewRateForword
                    isopen={isPopupOpen}
                    onClose={handleClose}
                    data={tableData}
                    reqBody={reqBody}
                />

            </div>
        </>
    )
}